"use client"

import { BUDDY_ETHERNET_PORT_ICON_1, BUDDY_ETHERNET_PORT_ICON_2, getConnectionWizardImage } from "@/lib/device-setup-assets"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog"
import { Home, Info } from "lucide-react"
import { useEffect, useMemo, useRef, useState } from "react"

import { Button } from "@/components/ui/button"
import Image from "next/image"
import Link from "next/link"
import { accountSupportsWifi } from "@/lib/device-capabilities"
import { useSearchParams } from "next/navigation"
import { useUser } from "@/components/providers/user-provider"

const BUDDY_STEPS = [
    {
        title: "Plug in the Curly Cord",
        text: "Plug the curly cord into the handset and the bottom of the base.",
        helper: "Not sure which port? You'll see a small handset icon in front of the proper port.",
    },
    {
        title: "Ethernet Cable",
        text: "Plug the Ethernet cable into the base of the phone.",
        helper: "Locate this icon on the bottom of the base of the phone. This is the proper port for the ethernet cable.",
        helperImages: [BUDDY_ETHERNET_PORT_ICON_1],
    },
    {
        title: "Connect to Your Router",
        text: "Temporarily plug the Ethernet cable into your router. Do not plug it into the port labeled WAN.",
        helper: "The correct Ethernet ports are typically surrounded in yellow, or marked with one of these symbols.\n\nNote: You will only need to plug the phone into the router for Wi-Fi set up. This is not the permanent location of your phone.",
        helperImages: [BUDDY_ETHERNET_PORT_ICON_1, BUDDY_ETHERNET_PORT_ICON_2],
    },
    {
        title: "Connect To Power",
        text: "1. Nest the handset on the base.\n2. Plug your phone into power.",
        helper: "Wait ~2 minutes.",
        showLED: true,
    },
    {
        title: "You're Connected!",
        text: "We're checking that your Wiley Buddy came online through the temporary ethernet connection.",
        subtext: "Once it shows online, you can unplug it from the router and move it anywhere within range of your Wi-Fi.",
        isFinal: true,
    },
]

const MAGIC_BOX_STEPS = [
    {
        title: "Ethernet Cable",
        text: "Plug the Ethernet cable into the blue port on back of the Magic Box.",
    },
    {
        title: "Connect to Internet Router",
        text: "Plug the other end of the Ethernet cable into your internet router (or any Ethernet port with active internet e.g. WiFi Extender, Ethernet Wall Port, etc).",
    },
    {
        title: "Connect To Power",
        text: "Now you can connect the Magic Box to power.",
        helper: "Wait for ~2 minutes until all 3 of the following symbols light up blue.",
        showIndicators: true,
    },
    {
        title: "Connect To Your Phone",
        text: "Plug your phone's telephone cord into the green port on the back of the Magic Box.",
    },
]

export default function SetupPage() {
    const searchParams = useSearchParams()
    const promoCode = searchParams.get("promo")
    const { onboardingComplete, profile } = useUser()
    const provisioningInstructionsRef = useRef<HTMLDivElement>(null)
    const [expandedImage, setExpandedImage] = useState<{ src: string; alt: string } | null>(null)
    const hasKnownCapabilities = !!profile?.deviceTypesByMac || !!profile?.onboardingData?.deviceType
    const supportsWifi = useMemo(
        () => accountSupportsWifi(profile?.deviceTypesByMac, profile?.onboardingData?.deviceType),
        [profile?.deviceTypesByMac, profile?.onboardingData?.deviceType]
    )

    const steps = useMemo(() => ([
        {
            title: "Device & Payment Details",
            description: "Enter your device info and billing details.",
        },
        {
            title: "E911 Address",
            description: "Set your physical address for emergency services.",
        },
        !hasKnownCapabilities
            ? {
                title: "Device Configuration",
                description: "Name your device and add network settings if your model supports Wi-Fi.",
            }
            : supportsWifi
            ? {
                title: "Device Configuration",
                description: "Name your device and enter Wi-Fi credentials.",
            }
            : {
                title: "Device Configuration",
                description: "Name your device. No wireless setup needed for this device.",
            },
    ]), [hasKnownCapabilities, supportsWifi])

    // Build the onboarding link — pass promo code through to the main app
    const onboardingHref = promoCode ? `/?promo=${encodeURIComponent(promoCode)}` : "/"

    // Scroll to and highlight "Plugging in Your Phone" if user has completed onboarding
    useEffect(() => {
        if (onboardingComplete && provisioningInstructionsRef.current) {
            setTimeout(() => {
                provisioningInstructionsRef.current?.scrollIntoView({ 
                    behavior: "smooth", 
                    block: "center" 
                })
            }, 300)
        }
    }, [onboardingComplete])

    return (
        <div className={`max-w-2xl mx-auto py-8 px-4 pb-[max(2rem,env(safe-area-inset-bottom))] ${!onboardingComplete ? "flex flex-col justify-center" : ""}`}>
            <div className="space-y-6">
                {/* Main card — matches onboarding WelcomeStep layout */}
                <Card className="border-sidebar-border bg-card/50 backdrop-blur">
                    <CardContent className="pt-6">
                        <div className="space-y-6">
                            <div className="text-center space-y-2">
                                <h2 className="text-2xl font-bold">Set Up Your Wiley Phone</h2>
                                <p className="text-muted-foreground">Here&apos;s what to expect — the whole process takes just a few minutes.</p>
                            </div>

                            {/* Steps */}
                            <ol className="space-y-4">
                                {steps.map((step, i) => (
                                    <li key={i} className="flex items-start gap-3">
                                        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-semibold">
                                            {i + 1}
                                        </span>
                                        <div>
                                            <p className="font-medium">{step.title}</p>
                                            <p className="text-sm text-muted-foreground">{step.description}</p>
                                        </div>
                                    </li>
                                ))}
                            </ol>

                            {/* MAC Address help */}
                            <div className="rounded-lg border border-dashed bg-muted/50 p-4">
                                <p className="text-sm font-medium flex items-center gap-2">
                                    <Info className="h-4 w-4" />
                                    Where is the MAC address?
                                </p>
                                <p className="text-sm text-muted-foreground mt-1">
                                    Flip your Wiley device over — you&apos;ll find a label on the <strong>bottom of the phone</strong> with a MAC address
                                    that looks like <span className="font-mono text-foreground">C074AD******</span>. You&apos;ll need this during setup.
                                </p>
                            </div>

                            {/* CTA */}
                            {!onboardingComplete ? (
                                <Button className="w-full" size="lg" asChild>
                                    <Link href={onboardingHref}>
                                        Let&apos;s Get Started
                                    </Link>
                                </Button>
                            ) : (
                                <Button className="w-full" size="lg" variant="outline" asChild>
                                    <Link href="/">
                                        <Home className="mr-2 h-4 w-4" />
                                        Back to Dashboard
                                    </Link>
                                </Button>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* The remaining setup/provisioning content is only shown after onboarding
                    is complete (i.e. inside the main app's setup guide). The initial
                    /setup landing page stops at the "Let's Get Started" CTA. */}
                {onboardingComplete && (<>

                {supportsWifi && (
                <Card className="border-sidebar-border bg-card/50 backdrop-blur">
                    <CardHeader>
                        <CardTitle>Wiley Buddy Setup Video</CardTitle>
                        <CardDescription>Watch the full setup walkthrough if you want a visual guide before you keep going.</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3">
                            <div className="overflow-hidden rounded-xl border bg-black shadow-sm">
                                <div className="relative aspect-video w-full">
                                    <iframe
                                        className="absolute inset-0 h-full w-full"
                                        src="https://www.youtube-nocookie.com/embed/_665e7Ai6ks"
                                        title="Wiley Buddy setup walkthrough"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                        referrerPolicy="strict-origin-when-cross-origin"
                                        allowFullScreen
                                    />
                                </div>
                            </div>
                            <p className="text-sm text-muted-foreground">
                                This video is only shown in the Setup Guide. The initial onboarding flow stays unchanged.
                            </p>
                        </div>
                    </CardContent>
                </Card>
                )}

                {/* Before You Start — matches onboarding StopStep */}
                <Card ref={provisioningInstructionsRef} className="border-sidebar-border bg-card/50 backdrop-blur">
                    <CardHeader>
                        <CardTitle>Before You Start</CardTitle>
                        <CardDescription>Important safety check</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-6">
                            <div className="flex flex-col items-center text-center space-y-4">
                                <div className="w-24 h-24 rounded-lg bg-red-500 flex items-center justify-center rotate-45 shadow-lg">
                                    <span className="-rotate-45 text-white font-extrabold text-2xl tracking-widest">STOP</span>
                                </div>
                                <h2 className="text-2xl font-bold mt-4">Before You Start</h2>
                                <p className="text-muted-foreground text-lg">
                                    Your phone needs to be plugged into your router via <strong>Ethernet first</strong>, then power.
                                </p>
                                <p className="text-sm text-muted-foreground">We&apos;ll walk you through each cable one at a time.</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Wiley Buddy Setup */}
                <div className="space-y-4">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Wiley Buddy Setup</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {BUDDY_STEPS.filter(s => !s.isFinal).map((step, i) => {
                            const image = getConnectionWizardImage("ghp610w", i)
                            const helperImages = "helperImages" in step ? step.helperImages : undefined
                            return (
                                <Card key={i} className="border-sidebar-border bg-card/50 backdrop-blur">
                                    <CardHeader className="pb-2">
                                        <CardDescription>Device connection</CardDescription>
                                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                            Step {i + 1} of {BUDDY_STEPS.filter(s => !s.isFinal).length}
                                        </p>
                                        <CardTitle className="text-base">{step.title}</CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-3">
                                        {image && (
                                            <button
                                                type="button"
                                                className="relative mx-auto aspect-square w-full max-w-xs overflow-hidden rounded-lg border bg-muted/10 cursor-zoom-in"
                                                onClick={() => setExpandedImage(image)}
                                            >
                                                <Image src={image.src} alt={image.alt} fill className="object-cover" sizes="(max-width: 672px) 40vw, 250px" />
                                            </button>
                                        )}
                                        <p className="text-sm text-muted-foreground whitespace-pre-line">{step.text}</p>
                                        {step.helper && <p className="text-xs text-muted-foreground italic whitespace-pre-line">{step.helper}</p>}
                                        {helperImages?.length ? (
                                            <div className="flex flex-wrap items-center gap-3">
                                                {helperImages.map((helperImage) => (
                                                    <div key={helperImage.src} className="relative h-18 w-18 overflow-hidden rounded-md border bg-background/80 p-2">
                                                        <Image src={helperImage.src} alt={helperImage.alt} fill className="object-contain p-2" sizes="72px" />
                                                    </div>
                                                ))}
                                            </div>
                                        ) : null}
                                        {step.showLED && (
                                            <div className="space-y-2">
                                                <p className="text-xs font-semibold">What you&apos;ll see:</p>
                                                <ul className="text-xs space-y-1.5">
                                                    <li className="flex items-start gap-2">
                                                        <span className="mt-0.5 h-3 w-3 shrink-0 rounded-full bg-red-500 animate-pulse" />
                                                        <span className="text-muted-foreground"><strong className="text-foreground">Red flashing</strong> — downloading configuration</span>
                                                    </li>
                                                    <li className="flex items-start gap-2">
                                                        <span className="mt-0.5 h-3 w-3 shrink-0 rounded-full bg-green-500 animate-pulse" />
                                                        <span className="text-muted-foreground"><strong className="text-foreground">Green flashing</strong> — applying new settings</span>
                                                    </li>
                                                    <li className="flex items-start gap-2">
                                                        <span className="mt-0.5 h-3 w-3 shrink-0 rounded-full bg-muted-foreground/30" />
                                                        <span className="text-muted-foreground"><strong className="text-foreground">Light goes out</strong> — setup complete!</span>
                                                    </li>
                                                </ul>
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            )
                        })}
                    </div>

                    {/* Buddy "You're Connected" card */}
                    <Card className="border-sidebar-border bg-card/50 backdrop-blur">
                        <CardContent className="pt-6">
                            <div className="text-center space-y-3">
                                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                    Step {BUDDY_STEPS.length} of {BUDDY_STEPS.length}
                                </p>
                                <h3 className="text-xl font-bold">You&apos;re Connected!</h3>
                                <p className="text-muted-foreground">
                                    We&apos;re checking that your Wiley Buddy came online through the temporary ethernet connection.
                                </p>
                                <p className="text-muted-foreground">
                                    Once it shows online, you can unplug it from the router and move it anywhere within range of your Wi-Fi.
                                </p>
                                <p className="text-sm text-muted-foreground">
                                    After this finishes, we&apos;ll take you to your account and remind you to record a voicemail greeting so callers hear your household name instead of extension 2001.
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Magic Box Setup */}
                <div className="space-y-4">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Magic Box Setup</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {MAGIC_BOX_STEPS.map((step, i) => {
                            const image = getConnectionWizardImage("ht801", i)
                            return (
                                <Card key={i} className="border-sidebar-border bg-card/50 backdrop-blur">
                                    <CardHeader className="pb-2">
                                        <CardDescription>Device connection</CardDescription>
                                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                            Step {i + 1} of {MAGIC_BOX_STEPS.length}
                                        </p>
                                        <CardTitle className="text-base">{step.title}</CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-3">
                                        {image && (
                                            <button
                                                type="button"
                                                className="relative mx-auto aspect-square w-full max-w-xs overflow-hidden rounded-lg border bg-muted/10 cursor-zoom-in"
                                                onClick={() => setExpandedImage(image)}
                                            >
                                                <Image src={image.src} alt={image.alt} fill className="object-cover" sizes="(max-width: 672px) 40vw, 250px" />
                                            </button>
                                        )}
                                        <p className="text-sm text-muted-foreground whitespace-pre-line">{step.text}</p>
                                        {step.helper && <p className="text-xs text-muted-foreground italic">{step.helper}</p>}
                                        {"showIndicators" in step && step.showIndicators && (
                                            <div className="space-y-2">
                                                <p className="text-xs font-semibold">Look for these indicators:</p>
                                                <div className="flex justify-center gap-6">
                                                    <div className="flex flex-col items-center gap-1">
                                                        <span className="h-6 w-6 rounded-full bg-blue-500" />
                                                        <span className="text-xs text-muted-foreground">Power</span>
                                                    </div>
                                                    <div className="flex flex-col items-center gap-1">
                                                        <span className="h-6 w-6 rounded-full bg-blue-500" />
                                                        <span className="text-xs text-muted-foreground">Internet</span>
                                                    </div>
                                                    <div className="flex flex-col items-center gap-1">
                                                        <span className="h-6 w-6 rounded-full bg-blue-500" />
                                                        <span className="text-xs text-muted-foreground">Phone</span>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            )
                        })}
                    </div>
                </div>

                </>)}

                {/* Image expand dialog */}
                <Dialog open={!!expandedImage} onOpenChange={(open) => { if (!open) setExpandedImage(null) }}>
                    <DialogContent className="sm:max-w-2xl p-2" showCloseButton>
                        <DialogTitle className="sr-only">{expandedImage?.alt}</DialogTitle>
                        {expandedImage && (
                            <div className="relative w-full aspect-4/3">
                                <Image src={expandedImage.src} alt={expandedImage.alt} fill className="object-contain rounded" sizes="(max-width: 672px) 95vw, 672px" />
                            </div>
                        )}
                        <p className="text-sm text-muted-foreground text-center px-2 pb-1">{expandedImage?.alt}</p>
                    </DialogContent>
                </Dialog>
            </div>
        </div>
    )
}
